/**
 * Abas de produto (m-product-tabs): sliders, load more e botão no header.
 *
 * Modificações em relação ao tema base:
 * - initHeaderButton(): inicializa o botão no header (data-header-button-wrapper).
 *   Se buttonType === "load", delega o clique em [data-load-more-product] para
 *   handleLoadMore(activeTab), usando o activeTabId guardado no wrapper.
 * - updateHeaderButton(tabId): mantém o botão do header em sync com a aba ativa.
 *   Tipo "link" → atualiza o href com a URL da coleção da aba; tipo "load" →
 *   grava activeTabId e url no wrapper para o load more. Chamado ao trocar de aba
 *   e na inicialização (primeira aba).
 * - Shopify.designMode: ao selecionar um bloco .m-tab-content no editor, define
 *   a aba ativa (setActiveTab) para o editor refletir a aba selecionada.
 */
if (!customElements.get("m-product-tabs")) {
  class MProductTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        loadMoreBtn: "[data-load-more-product]",
        productsContainer: "[data-products-container]",
        tabContent: [".m-tab-content"],
      };
      this.tabSliders = [];
      this.sliderEnabled = this.dataset.enableSlider === "true";
      this.mobileSliderDisable = this.dataset.mobileDisableSlider === "true";
      this.buttonType = this.dataset.buttonType;
      this.domNodes = queryDomNodes(this.selectors, this);
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.autoplay = this.dataset.autoplay === "true";
      this.timeout = this.dataset.timeout;
      this.items = this.dataset.items;
      this.sharedControls = null;
      this.useSharedSliderControls = false;
      this.activeSlider = null;
      this.sharedControlsBound = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.sharedControls = this.querySelector(".m-product-tabs__header-controls");
      this.useSharedSliderControls = !!this.sharedControls;

      if (this.sliderEnabled) {
        for (let block of this.domNodes.tabContent) {
          this.initSliderByScreenSize(block);
          document.addEventListener("matchMobile", () => {
            this.initSliderByScreenSize(block);
          });
          document.addEventListener("unmatchMobile", () => {
            this.initSliderByScreenSize(block);
          });
        }
      }
      if (!this.sliderEnabled && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;

        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;
        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      document.addEventListener("matchMobile", () => {
        if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
          this.canLoad = true;
          this.currentPage = 1;
          for (let block of this.domNodes.tabContent) {
            this.initLoadMore(block);
          }
        }
      });
      this.initTabs();
      this.initMobileSelect();
      this.initHeaderButton();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (event) => {
          const blockSelectedIsTab = event.target.classList.contains("m-tab-content");
          if (!blockSelectedIsTab) return;
          const dataIndex = event.target && event.target.dataset.index;
          this.tabs.setActiveTab(dataIndex);
        });
      }
    }

    initHeaderButton() {
      const headerButtonWrappers = this.querySelectorAll("[data-header-button-wrapper]");
      if (!headerButtonWrappers.length) return;
      
      if (this.buttonType === "load") {
        headerButtonWrappers.forEach((headerButtonWrapper) => {
          addEventDelegate({
            context: headerButtonWrapper,
            selector: "[data-load-more-product]",
            handler: (e) => {
              e.preventDefault();
              const activeTabId = headerButtonWrapper.dataset.activeTabId;
              if (activeTabId) {
                const activeTab = this.querySelector("#" + activeTabId);
                if (activeTab) {
                  this.handleLoadMore(activeTab);
                }
              }
            },
          });
        });
      }
    }

    initTabs() {
      this.tabs = new MinimogTheme.Tabs(this, (target) => {
        const tabId = target.getAttribute("href");
        const tabContent = this.querySelector(tabId);
        const slider = this.querySelector(tabId + " .swiper-container");
        const controlsContainer = this.getControlsContainer(tabContent);
        // trigger update slider
        slider && slider.swiper && slider.swiper.update();
        if (slider && slider.swiper) {
          this.activeSlider = slider.swiper;
        }
        const firstItem = slider && (slider.querySelector(".m-image") || slider.querySelector(".m-placeholder-svg"));
        if (firstItem && controlsContainer) {
          const itemHeight = firstItem.clientHeight;
          controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");
        }
        
        // Update header button if it exists
        this.updateHeaderButton(tabId);
        this.setActiveSliderControls(tabContent);
      });
      
      // Initialize header button with first tab
      if (this.domNodes.tabContent.length > 0) {
        const firstTab = this.domNodes.tabContent[0];
        const firstTabId = firstTab.getAttribute("id");
        this.updateHeaderButton("#" + firstTabId);
        this.setActiveSliderControls(firstTab);
      }
    }

    updateHeaderButton(tabId) {
      const headerButtonWrappers = this.querySelectorAll("[data-header-button-wrapper]");
      if (!headerButtonWrappers.length) return;
      
      const activeTab = this.querySelector(tabId);
      if (!activeTab) return;
      
      const collectionUrl = activeTab.dataset.url;

      headerButtonWrappers.forEach((headerButtonWrapper) => {
        const headerButton = headerButtonWrapper.querySelector("[data-header-button]");
        if (!headerButton) return;

        if (this.buttonType === "link") {
          headerButton.href = collectionUrl;
        } else if (this.buttonType === "load") {
          headerButtonWrapper.dataset.activeTabId = activeTab.id;
          headerButtonWrapper.dataset.url = collectionUrl;
        }
      });
    }

    initSliderByScreenSize(sliderContainer) {
      const mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      const slider = sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = this.getControlsContainer(sliderContainer);

      if (MinimogTheme.config.mqlMobile && mobileDisableSlider) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        slider.classList.remove("swiper-container");
        if (slider.swiper) slider.swiper.destroy(false, true);
      } else {
        controlsContainer && controlsContainer.classList.remove("m:hidden");
        setTimeout(() => {
          this.initSlider(sliderContainer);
        });
      }
    }

    getControlsContainer(sliderContainer) {
      if (this.useSharedSliderControls) return this.sharedControls;
      return sliderContainer && sliderContainer.querySelector(".m-slider-controls");
    }

    bindSharedSliderControls() {
      if (!this.useSharedSliderControls || this.sharedControlsBound || !this.sharedControls) return;

      const prevButton = this.sharedControls.querySelector(".m-slider-controls__button-prev");
      const nextButton = this.sharedControls.querySelector(".m-slider-controls__button-next");

      prevButton &&
        prevButton.addEventListener("click", () => {
          if (this.activeSlider) this.activeSlider.slidePrev();
        });
      nextButton &&
        nextButton.addEventListener("click", () => {
          if (this.activeSlider) this.activeSlider.slideNext();
        });

      this.sharedControlsBound = true;
    }

    setActiveSliderControls(sliderContainer) {
      if (!this.useSharedSliderControls || !sliderContainer) return;

      const slider = sliderContainer.querySelector(".m-mixed-layout__wrapper");
      if (slider && slider.swiper) {
        this.activeSlider = slider.swiper;
      }

      const slideItemsLength =
        sliderContainer.querySelector(".swiper-wrapper") && sliderContainer.querySelector(".swiper-wrapper").childElementCount;
      if (slideItemsLength && parseInt(this.items) >= slideItemsLength) {
        this.sharedControls && this.sharedControls.classList.add("m:hidden");
      } else {
        this.sharedControls && this.sharedControls.classList.remove("m:hidden");
      }

      if (this.showPagination && this.activeSlider && this.sharedControls) {
        const pagination = this.sharedControls.querySelector(".swiper-pagination");
        if (pagination && this.activeSlider.params && this.activeSlider.params.pagination) {
          this.activeSlider.params.pagination.el = pagination;
          if (this.activeSlider.pagination) {
            this.activeSlider.pagination.el = pagination;
            this.activeSlider.pagination.render && this.activeSlider.pagination.render();
            this.activeSlider.pagination.update && this.activeSlider.pagination.update();
          }
        }
      }

      const firstItem = sliderContainer.querySelector(".m-image") || sliderContainer.querySelector(".m-placeholder-svg");
      if (firstItem && this.sharedControls) {
        this.sharedControls.style.setProperty("--offset-top", parseInt(firstItem.clientHeight) / 2 + "px");
      }
    }

    initSlider(sliderContainer) {
      const layoutWrapper = sliderContainer.querySelector(".m-product-list");
      const swiper = sliderContainer && sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = this.getControlsContainer(sliderContainer);
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const slideItemsLength =
        sliderContainer &&
        sliderContainer.querySelector(".swiper-wrapper") &&
        sliderContainer.querySelector(".swiper-wrapper").childElementCount;
      const isActiveTab = sliderContainer.classList.contains("active") || sliderContainer.classList.contains("open");
      if (parseInt(this.items) >= slideItemsLength) {
        if (!this.useSharedSliderControls || isActiveTab) {
          controlsContainer && controlsContainer.classList.add("m:hidden");
        }
        layoutWrapper.classList.add("m-mixed-layout--mobile-grid");
        return;
      }

      swiper && swiper.classList.add("swiper-container");

      let slider = new MinimogLibs.Swiper(swiper, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.enableFlashsale ? false : true,
        autoplay: this.autoplay
          ? {
              delay: parseInt(this.timeout),
              disableOnInteraction: false,
            }
          : false,
        pagination: this.showPagination
          ? {
              el: this.useSharedSliderControls
                ? this.sharedControls && this.sharedControls.querySelector(".swiper-pagination")
                : sliderContainer.querySelector(".swiper-pagination"),
              type: "bullets",
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          992: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        threshold: 2,
        on: {
          init: () => {
            setTimeout(() => {
              // Calculate controls position
              const firstItem =
                sliderContainer.querySelector(".m-image") || sliderContainer.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton && prevButton.classList.remove("m:hidden");
                nextButton && nextButton.classList.remove("m:hidden");
              }
              if (this.useSharedSliderControls && (isActiveTab || !this.activeSlider)) {
                this.activeSlider = slider;
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (this.useSharedSliderControls) {
        this.bindSharedSliderControls();
        if (isActiveTab || !this.activeSlider) this.activeSlider = slider;
      } else if (slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => slider.slideNext());
      }
    }

    initMobileSelect() {
      this.select = this.querySelector("[data-tab-select]");
      this.select.addEventListener("change", () => {
        this.tabs.setActiveTab(parseInt(this.select.value));
        const slider = this.tabs && this.tabs.currentTab && this.tabs.currentTab.querySelector(".swiper-container");
        const controlsContainer = this.tabs && this.tabs.currentTab && this.getControlsContainer(this.tabs.currentTab);
        const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
        const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
        slider && slider.swiper && slider.swiper.update();
        if (slider && slider.swiper) {
          this.activeSlider = slider.swiper;
        }
        const firstItem = slider && slider.querySelector(".m-image");
        if (firstItem && controlsContainer) {
          const itemHeight = firstItem.clientHeight;
          controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

          prevButton && prevButton.classList.remove("m:hidden");
          nextButton && nextButton.classList.remove("m:hidden");
        }
      });
    }

    initLoadMore(wrapper) {
      addEventDelegate({
        context: wrapper,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore(wrapper);
        },
      });
    }

    handleLoadMore(wrapper) {
      const loadBtn = wrapper.querySelector(this.selectors.loadMoreBtn);
      const productsContainer = wrapper.querySelector(this.selectors.productsContainer);

      let currentPage = wrapper.dataset.page;
      currentPage = parseInt(currentPage);
      const totalPages = wrapper.dataset.totalPages;
      this.toggleLoading(loadBtn, true);

      const url = wrapper.dataset.url;
      const dataUrl = `${url}?page=${currentPage + 1}&section_id=${this.id}`;
      fetchCache(dataUrl).then((html) => {
        currentPage++;
        wrapper.dataset.page = currentPage;
        this.toggleLoading(loadBtn, false);
        const dom = generateDomFromString(html);
        const tabId = wrapper.getAttribute("id");
        const products = dom.querySelector(`#${tabId} ${this.selectors.productsContainer}`);

        if (products) {
          Array.from(products.childNodes).forEach((product) => productsContainer.appendChild(product));
        }

        if (currentPage >= parseInt(totalPages)) loadBtn && loadBtn.remove();
      });

      // Remove button focus
      loadBtn.blur();
    }

    toggleLoading(loadBtn, status) {
      if (!loadBtn) return;
      if (status) {
        loadBtn.classList.add("m-spinner-loading");
      } else {
        loadBtn.classList.remove("m-spinner-loading");
      }
    }
  }
  customElements.define("m-product-tabs", MProductTabs);
}
